import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
} from '@mui/material';
import { UsbOff } from '@mui/icons-material';

interface DeviceReconnectDialogProps {
  open: boolean;
  /** Optional body message. Defaults to a generic "reconnect to continue" message. */
  message?: string;
  reconnecting: boolean;
  reconnectError: string | null;
  onReconnect: () => void;
  onCancelTest: () => void;
}

/**
 * Shared blocking dialog for when the device disconnects before or between
 * measurements (pre-measurement wizard steps, incubation phase).
 *
 * Does NOT handle mid-measurement recovery — that is MeasurementInterruptedModal's
 * responsibility (which has RESEND/harvest/discard logic).
 */
export function DeviceReconnectDialog({
  open,
  message,
  reconnecting,
  reconnectError,
  onReconnect,
  onCancelTest,
}: DeviceReconnectDialogProps) {
  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <UsbOff sx={{ fontSize: 72, color: 'warning.main' }} />
        </Box>
        <Typography variant="h5" component="div">
          Reader Disconnected
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        <Typography variant="body1" color="text.secondary" paragraph>
          {message ??
            'The reader was disconnected. Reconnect it to continue — your progress has been preserved.'}
        </Typography>

        {reconnectError && (
          <Alert severity="error" sx={{ mt: 1, mb: 1, textAlign: 'left' }}>
            {reconnectError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancelTest}
          disabled={reconnecting}
        >
          Cancel Test
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onReconnect}
          disabled={reconnecting}
          startIcon={reconnecting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {reconnecting ? 'Reconnecting…' : 'Reconnect Reader'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
